import { useEffect } from 'react';
import { oneOf } from '../utils/storage';
import { LANGUAGES, t, type Language } from '../utils/i18n';
import { BACKGROUND_THEMES, isDarkModeActive, type BackgroundTheme, type ThemeMode } from '../utils/themeStyles';
import { usePersistentState } from './usePersistentState';

const BACKGROUNDS = Object.keys(BACKGROUND_THEMES) as BackgroundTheme[];

/** Alle bewaarde voorkeuren, plus het toepassen ervan op de pagina (donkere modus, thema, taal, titel). */
export function useSettings() {
  const [language, setLanguage] = usePersistentState<Language>('app-language', 'nl', oneOf(LANGUAGES));
  const [mode, setMode] = usePersistentState<ThemeMode>('app-theme-mode', 'auto', oneOf(['light', 'dark', 'auto']));
  const [background, setBackground] = usePersistentState<BackgroundTheme>('app-theme-bg', 'mesh_aurora', oneOf(BACKGROUNDS));

  // Licht/donker. In 'auto' volgen we het systeem én de klok (20:00-07:00), ook terwijl de app open staat.
  useEffect(() => {
    const update = () => {
      const dark = isDarkModeActive(mode) || background === 'midnight_velvet';
      document.documentElement.classList.toggle('dark', dark);
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#090d16' : '#eef2f6');
    };

    update();
    if (mode !== 'auto') return;

    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    mediaQuery?.addEventListener('change', update);
    const interval = window.setInterval(update, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') update();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mediaQuery?.removeEventListener('change', update);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [mode, background]);

  // Achtergrondthema
  useEffect(() => {
    document.documentElement.dataset.theme = background;
  }, [background]);

  // Taal van de pagina: html lang, titel en omschrijving (voor schermlezers, tabbladen en zoekmachines)
  useEffect(() => {
    document.documentElement.lang = language;
    document.title = t(language, 'metaTitle');
    document.querySelector('meta[name="description"]')?.setAttribute('content', t(language, 'metaDescription'));
  }, [language]);

  return { language, setLanguage, mode, setMode, background, setBackground };
}

export type Settings = ReturnType<typeof useSettings>;
